import { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { useSEO } from "@/lib/useSEO";
import { api, formatApiErrorDetail } from "@/lib/api";
import { fireAdsPurchase } from "@/lib/ads";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar, MapPin, Users, ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { SignInRequiredGate } from "@/components/SignInRequiredGate";

function formatWhen(iso) {
  if (!iso) return "Date & time TBA";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" }) +
      " · " + d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
}

function isPast(starts_at, ends_at) {
  if (!starts_at) return false;  // TBA → never treated as past
  const end = ends_at ? new Date(ends_at) : new Date(new Date(starts_at).getTime() + 2 * 60 * 60 * 1000);
  return end < new Date();
}

export default function MeetupDetail() {
  const { slug } = useParams();
  const [search] = useSearchParams();
  const paymentParam = search.get("payment");
  const { user } = useAuth();

  const [m, setM] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", age: "",
    instagram_handle: "", facebook_handle: "",
    waiver_accepted: false,
  });

  // Pre-fill name/email from signed-in user
  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        full_name: f.full_name || user.name || "",
        email: f.email || user.email || "",
      }));
    }
  }, [user]);

  useSEO({
    title: m ? `${m.title} — Community Meetup | Better Through Boxing` : "Community Meetup | Better Through Boxing",
    description: m ? (`${m.subtitle || m.title}. ${m.location_name} on ${formatWhen(m.starts_at)}. Register online for this Better Through Boxing community event.`) : "Boxing community meetup hosted by Coach Chris Persaud and The Flow State Method LLC.",
    path: `/meetups/${slug}`,
    keywords: m?.keywords?.length ? m.keywords : ["boxing meetup", "community boxing class", "free boxing event", "Coach Chris meetup"],
    ogImage: m?.cover_image,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/meetups/${slug}`);
        if (!cancelled) setM(data);
      } catch (e) {
        if (!cancelled) setM(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  // Inject Event JSON-LD schema once the meetup loads
  useEffect(() => {
    if (!m) return;
    const id = "meetup-jsonld";
    const existing = document.getElementById(id);
    if (existing) existing.remove();
    const free = !m.price_cents || m.price_cents === 0;
    const schema = {
      "@context": "https://schema.org",
      "@type": "Event",
      name: m.title,
      description: m.subtitle || m.description?.slice(0, 200),
      startDate: m.starts_at,
      endDate: m.ends_at || undefined,
      eventStatus: "https://schema.org/EventScheduled",
      eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
      location: {
        "@type": "Place",
        name: m.location_name,
        address: m.location_address,
      },
      image: m.cover_image ? [m.cover_image] : undefined,
      organizer: {
        "@type": "Organization",
        name: "The Flow State Method LLC",
        url: "https://betterthroughboxing.com",
      },
      offers: {
        "@type": "Offer",
        url: `https://betterthroughboxing.com/meetups/${m.slug}`,
        price: free ? "0" : (m.price_cents / 100).toFixed(2),
        priceCurrency: "USD",
        availability: "https://schema.org/InStock",
        validFrom: m.starts_at,
      },
    };
    const tag = document.createElement("script");
    tag.id = id;
    tag.type = "application/ld+json";
    tag.text = JSON.stringify(schema);
    document.head.appendChild(tag);
    return () => { const el = document.getElementById(id); if (el) el.remove(); };
  }, [m]);

  // Show toast based on Stripe redirect
  useEffect(() => {
    if (paymentParam === "success") {
      toast.success("Payment received — you're locked in! Check your email.");
      fireAdsPurchase({ transactionId: search.get("session_id") });
    }
    if (paymentParam === "cancel") toast.error("Payment cancelled. You can register again any time.");
  }, [paymentParam]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.waiver_accepted) {
      toast.error("Please accept the liability waiver to register.");
      return;
    }
    if (!form.full_name || !form.email || !form.phone || !form.age) {
      toast.error("All required fields must be filled.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/meetups/${slug}/register`, {
        ...form,
        age: parseInt(form.age, 10),
        origin_url: window.location.origin,
      });
      if (data.free) {
        toast.success("You're registered! Confirmation email sent.");
        setForm({ full_name: "", email: "", phone: "", age: "", instagram_handle: "", facebook_handle: "", waiver_accepted: false });
      } else if (data.checkout_url) {
        toast.info("Redirecting to secure checkout...");
        window.location.href = data.checkout_url;
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(formatApiErrorDetail(detail) || "Registration failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="App"><SiteHeader />
        <main className="max-w-4xl mx-auto px-6 md:px-10 py-24 text-center" data-testid="meetup-loading">
          <Loader2 className="h-8 w-8 animate-spin text-[#FF331F] mx-auto" />
        </main>
      <SiteFooter /></div>
    );
  }

  if (!m) {
    return (
      <div className="App"><SiteHeader />
        <main className="max-w-3xl mx-auto px-6 md:px-10 py-24" data-testid="meetup-not-found">
          <h1 className="font-display uppercase text-4xl mb-4">Meetup not found.</h1>
          <p className="text-zinc-400 mb-6">This event may have been removed or unpublished.</p>
          <Link to="/meetups" className="inline-flex items-center text-[#FF331F] font-bold uppercase tracking-widest text-xs">
            <ArrowLeft className="h-3 w-3 mr-2" /> See all meetups
          </Link>
        </main>
      <SiteFooter /></div>
    );
  }

  const free = !m.price_cents || m.price_cents === 0;
  const past = isPast(m.starts_at, m.ends_at);
  const capacityReached = m.capacity && m.registration_count >= m.capacity;
  const mapUrl = m.location_map_url || (m.location_address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(m.location_address)}` : null);

  return (
    <div className="App relative">
      <SiteHeader />
      <main className="max-w-5xl mx-auto px-6 md:px-10 py-12 md:py-16" data-testid="meetup-detail">
        <Link to="/meetups" className="inline-flex items-center text-zinc-400 hover:text-white text-xs uppercase tracking-widest mb-8">
          <ArrowLeft className="h-3 w-3 mr-2" /> Back to meetups
        </Link>

        <div className="grid md:grid-cols-5 gap-10">
          {/* Cover + description */}
          <div className="md:col-span-3 space-y-8">
            {m.cover_image && (
              <div className="relative aspect-[16/9] overflow-hidden border border-[#27272A] bg-[#0F0F0F]">
                <img src={m.cover_image} alt={m.title} className={`w-full h-full object-cover ${past ? "grayscale" : ""}`} />
                {past && <div className="absolute top-3 left-3 bg-zinc-900/90 text-white text-[10px] uppercase tracking-widest font-bold px-2 py-1 border border-zinc-700">Past Event</div>}
              </div>
            )}
            <div>
              <div className="text-[#FF331F] text-xs uppercase tracking-[0.3em] font-bold mb-3">Community Meetup</div>
              <h1 className="font-display font-bold uppercase text-4xl md:text-6xl tracking-tight leading-none mb-4">{m.title}</h1>
              {m.subtitle && <p className="text-zinc-300 text-lg leading-relaxed mb-4">{m.subtitle}</p>}
              {m.tags?.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-6">
                  {m.tags.map((t) => (
                    <span key={t} className="text-[10px] uppercase tracking-widest text-zinc-300 bg-[#1A1A1A] border border-[#27272A] px-2 py-1">{t}</span>
                  ))}
                </div>
              )}
            </div>

            <div className="prose prose-invert max-w-none">
              {(m.description || "").split(/\n\n+/).map((para, i) => (
                <p key={i} className="text-zinc-300 leading-relaxed mb-4 whitespace-pre-line">{para}</p>
              ))}
            </div>

            {m.gallery?.length > 0 && (
              <div data-testid="meetup-gallery">
                <h2 className="font-display uppercase text-xl mb-4 text-zinc-300">Photos</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {m.gallery.map((src, i) => (
                    <a key={src} href={src} target="_blank" rel="noreferrer" className="block aspect-square overflow-hidden bg-[#1A1A1A] border border-[#27272A]">
                      <img src={src} alt={`${m.title} — ${i + 1}`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sticky info + registration */}
          <aside className="md:col-span-2 md:sticky md:top-24 md:self-start space-y-6">
            <div className="border border-[#27272A] bg-[#0F0F0F] p-6 space-y-4" data-testid="meetup-info">
              <div className="flex items-center justify-between border-b border-[#27272A] pb-4">
                <div className="text-[#FF331F] font-display uppercase text-2xl tracking-tight">
                  {free ? "Free" : `$${(m.price_cents / 100).toFixed(2)}`}
                </div>
                {past && <div className="text-[10px] uppercase tracking-widest text-zinc-400 bg-zinc-900 border border-zinc-700 px-2 py-1">Past Event</div>}
              </div>
              <div className="flex items-start gap-3 text-sm">
                <Calendar className="h-4 w-4 text-[#FF331F] mt-0.5 shrink-0" />
                <div>
                  <div className="text-zinc-400 text-[10px] uppercase tracking-widest font-bold mb-1">When</div>
                  <div className="text-white">{formatWhen(m.starts_at)}</div>
                </div>
              </div>
              <div className="flex items-start gap-3 text-sm">
                <MapPin className="h-4 w-4 text-[#FF331F] mt-0.5 shrink-0" />
                <div>
                  <div className="text-zinc-400 text-[10px] uppercase tracking-widest font-bold mb-1">Where</div>
                  <div className="text-white">{m.location_name || <span className="text-zinc-500 italic">Location TBA</span>}</div>
                  {m.location_address && <div className="text-zinc-500 text-xs">{m.location_address}</div>}
                  {mapUrl && <a href={mapUrl} target="_blank" rel="noreferrer" className="text-[#FF331F] text-xs hover:underline mt-1 inline-block">Open in Google Maps →</a>}
                </div>
              </div>
              {m.capacity ? (
                <div className="flex items-start gap-3 text-sm">
                  <Users className="h-4 w-4 text-[#FF331F] mt-0.5 shrink-0" />
                  <div>
                    <div className="text-zinc-400 text-[10px] uppercase tracking-widest font-bold mb-1">Capacity</div>
                    <div className="text-white">{m.registration_count || 0} / {m.capacity} spots filled</div>
                  </div>
                </div>
              ) : null}
            </div>

            {past ? (
              <div className="border border-zinc-800 bg-[#0F0F0F] p-6 text-center">
                <div className="text-zinc-400 text-sm">This event has ended. Browse <Link to="/meetups" className="text-[#FF331F] hover:underline">upcoming meetups</Link>.</div>
              </div>
            ) : capacityReached ? (
              <div className="border border-[#FF331F]/50 bg-[#FF331F]/10 p-6 text-center" data-testid="meetup-full">
                <div className="text-white font-display uppercase text-xl mb-2">Sold Out</div>
                <div className="text-zinc-300 text-sm">This meetup is at full capacity. Follow <a href="https://www.instagram.com/flow_state_method/" target="_blank" rel="noreferrer" className="text-[#FF331F] hover:underline">@flow_state_method</a> for the next one.</div>
              </div>
            ) : !user ? (
              <SignInRequiredGate
                title={free ? "Sign in to reserve your spot" : `Sign in to register & pay ($${(m.price_cents / 100).toFixed(2)})`}
                subtitle="Meetup registrations sit in your account so you can see what you're signed up for and get reminders."
                testid="meetup-signin-gate"
              />
            ) : (
              <form onSubmit={onSubmit} className="border border-[#27272A] bg-[#0F0F0F] p-6 space-y-4" data-testid="meetup-register-form">
                <div className="font-display uppercase text-lg">{free ? "Reserve your spot" : "Register & pay"}</div>
                <Field id="full_name" label="Full name *" value={form.full_name} onChange={(v) => setForm({ ...form, full_name: v })} placeholder="Jane Athlete" testid="reg-name" />
                <Field id="email" label="Email *" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} placeholder="jane@example.com" testid="reg-email" />
                <Field id="phone" label="Phone *" type="tel" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} placeholder="(555) 123-4567" testid="reg-phone" />
                <Field id="age" label="Age *" type="number" value={form.age} onChange={(v) => setForm({ ...form, age: v })} placeholder="28" testid="reg-age" />
                <Field id="instagram_handle" label="Instagram (optional)" value={form.instagram_handle} onChange={(v) => setForm({ ...form, instagram_handle: v })} placeholder="@yourhandle" testid="reg-instagram" />
                <Field id="facebook_handle" label="Facebook (optional)" value={form.facebook_handle} onChange={(v) => setForm({ ...form, facebook_handle: v })} placeholder="facebook.com/you" testid="reg-facebook" />
                <div className="flex items-start gap-2 pt-2">
                  <Checkbox id="waiver" checked={form.waiver_accepted} onCheckedChange={(c) => setForm({ ...form, waiver_accepted: !!c })} data-testid="reg-waiver" className="mt-0.5 border-zinc-600 data-[state=checked]:bg-[#FF331F] data-[state=checked]:border-[#FF331F]" />
                  <Label htmlFor="waiver" className="text-xs text-zinc-400 leading-snug cursor-pointer">
                    I have read and accept the <Link to="/waiver" target="_blank" className="text-[#FF331F] hover:underline">liability waiver</Link> and understand the risks of boxing training. *
                  </Label>
                </div>
                <Button type="submit" disabled={submitting} className="w-full bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest rounded-sm" data-testid="reg-submit">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  {free ? "Reserve my spot" : `Pay $${(m.price_cents / 100).toFixed(2)} & register`}
                </Button>
                <p className="text-[10px] text-zinc-500 leading-relaxed">By registering you confirm the info above is accurate. We&apos;ll email you confirmation and any updates.</p>
              </form>
            )}
          </aside>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}

function Field({ id, label, value, onChange, placeholder, type = "text", testid }) {
  return (
    <div>
      <Label htmlFor={id} className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">{label}</Label>
      <Input id={id} type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid}
        className="bg-[#050505] border-[#27272A] text-white mt-1 rounded-sm" />
    </div>
  );
}
